import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "../../components/Box";
import { Helpers } from "../../helpers/Helpers";
import { KTCard } from "../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../_metronic/helpers/components/KTCardBody";
import Content from "../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../_metronic/layout/core";
import { storeActivityIndicator } from "../../redux/reducers/authSlice";
import { RootState } from "../../redux/store";
import { MOBIL_SURUM_SAYFASI, MobilSurumItem, MobilSurumService } from "../../services/MobilSurumService";
import { surumGecerli } from "../../helpers/mobilSurum";

type Form = { sonSurum: string, enDusukSurum: string, magazaAdresi: string, aciklama: string };

const bosForm: Form = { sonSurum: '', enDusukSurum: '', magazaAdresi: '', aciklama: '' };

const formdan = (x: MobilSurumItem): Form => ({
    sonSurum: x.sonSurum ?? '',
    enDusukSurum: x.enDusukSurum ?? '',
    magazaAdresi: x.magazaAdresi ?? '',
    aciklama: x.aciklama ?? '',
});

/**
 * Ayarlar > Mobil Sürüm (04.11.2026, Burak, #544): Android ve iOS için son sürüm, en düşük (zorunlu) sürüm, mağaza adresi.
 * Uygulama her açılışta sorar: son sürümden eskiyse güncelleme penceresi, en düşükten eskiyse kapatılamayan pencere.
 * Her platform kendi Kaydet düğmesiyle kaydedilir.
 */
const MobilSurumAyar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const auth = useSelector((state: RootState) => state.auth);
    const pageName = MOBIL_SURUM_SAYFASI;
    const guncelleyebilir = !!auth.user.user?.data.authorizedMenus.find(x => x.menuName == pageName)?.canUpdate;

    const [kayitlar, setKayitlar] = useState<MobilSurumItem[]>([]);
    const [formlar, setFormlar] = useState<Record<string, Form>>({});

    useEffect(() => {
        getir();
    }, [])

    const yukle = (liste: MobilSurumItem[]) => {
        setKayitlar(liste);
        setFormlar(Object.fromEntries(liste.map(x => [x.platform, formdan(x)])));
    }

    const getir = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await MobilSurumService.liste();

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            yukle(response.data ?? []);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const alan = (platform: string, ad: keyof Form, deger: string) =>
        setFormlar({ ...formlar, [platform]: { ...(formlar[platform] ?? bosForm), [ad]: deger } });

    const kaydet = async (platform: string) => {
        const f = formlar[platform] ?? bosForm;

        if (!surumGecerli(f.sonSurum) || !surumGecerli(f.enDusukSurum)) {
            toast.error('Sürümü 4.13 biçiminde giriniz.');
            return;
        }

        try {
            dispatch(storeActivityIndicator(true));

            var response = await MobilSurumService.kaydet({
                platform,
                sonSurum: f.sonSurum.trim() || undefined,
                enDusukSurum: f.enDusukSurum.trim() || undefined,
                magazaAdresi: f.magazaAdresi.trim() || undefined,
                aciklama: f.aciklama.trim() || undefined,
            });

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            const yeni: MobilSurumItem = response.data;
            setKayitlar(kayitlar.map(x => x.platform == platform ? yeni : x));
            setFormlar({ ...formlar, [platform]: formdan(yeni) });
            toast.success(yeni.platformAdi + ' sürüm bilgisi kaydedildi.');
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const ozet = (f: Form) => {
        if (!f.sonSurum.trim())
            return <span><b>Kontrol kapalı.</b> Son sürüm boş olduğu için uygulama güncelleme sormaz.</span>;

        return (
            <span>
                <b>{f.sonSurum.trim()}</b>'den eski uygulamalar açılışta "Yeni sürüm mevcut" penceresi görür.{' '}
                {f.enDusukSurum.trim()
                    ? <><b>{f.enDusukSurum.trim()}</b>'den eskilerde güncelleme <b>zorunlu</b>: pencere kapatılamaz.</>
                    : 'Güncelleme zorunlu değil; kullanıcı "Sonra" diyebilir.'}
            </span>
        );
    }

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    {
                        kayitlar.map((k, i) => {
                            const f = formlar[k.platform] ?? bosForm;
                            const id = (ad: string) => `mobil-surum-${k.platform}-${ad}`;

                            return (
                                <KTCard key={k.platform} className={'ebbe-card' + (i > 0 ? ' mt-10' : '')}>
                                    <KTCardBody>
                                        <h4 className="mb-10 text-black">{k.platformAdi}</h4>

                                        <div className="row">
                                            <div className="col-md-3">
                                                <label className="form-label" htmlFor={id('son')}>Son sürüm</label>
                                                <input id={id('son')} className="form-control form-control-sm" placeholder="4.13" value={f.sonSurum}
                                                    disabled={!guncelleyebilir} onChange={e => alan(k.platform, 'sonSurum', e.target.value)} />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label" htmlFor={id('enDusuk')}>En düşük sürüm (zorunlu güncelleme)</label>
                                                <input id={id('enDusuk')} className="form-control form-control-sm" placeholder="Boş: zorunlu değil" value={f.enDusukSurum}
                                                    disabled={!guncelleyebilir} onChange={e => alan(k.platform, 'enDusukSurum', e.target.value)} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor={id('magaza')}>Mağaza adresi</label>
                                                <input id={id('magaza')} className="form-control form-control-sm"
                                                    placeholder={k.platform == 'ios' ? 'https://apps.apple.com/... ya da TestFlight bağlantısı' : 'https://play.google.com/store/apps/details?id=com.ebbedev.cargotrackingmobile'}
                                                    value={f.magazaAdresi} disabled={!guncelleyebilir} onChange={e => alan(k.platform, 'magazaAdresi', e.target.value)} />
                                            </div>
                                        </div>

                                        <div className="row mt-6">
                                            <div className="col-md-12">
                                                <label className="form-label" htmlFor={id('aciklama')}>Açıklama (güncelleme penceresinde görünür)</label>
                                                <textarea id={id('aciklama')} className="form-control form-control-sm" rows={2} maxLength={500} value={f.aciklama}
                                                    disabled={!guncelleyebilir} onChange={e => alan(k.platform, 'aciklama', e.target.value)} />
                                            </div>
                                        </div>

                                        <div className="p-4 rounded bg-light-primary fs-6 mt-6">{ozet(f)}</div>

                                        <div className="d-flex justify-content-between align-items-center mt-6">
                                            <span className="text-muted fs-8">
                                                {k.guncelleyen ? `Son güncelleyen: ${k.guncelleyen} · ${k.guncellemeTarihi}` : ''}
                                            </span>
                                            {
                                                guncelleyebilir && (
                                                    <button id={id('kaydet')} className="btn btn-primary btn-sm" onClick={() => kaydet(k.platform)}>Kaydet</button>
                                                )
                                            }
                                        </div>
                                    </KTCardBody>
                                </KTCard>
                            );
                        })
                    }

                    <div className="text-muted fs-7 mt-6">
                        Uygulama her açılışta bu ekrandaki sürüme bakar. Android'de güncelleme Google Play'den uygulama içinde iner (yalnız Play'den kurulan uygulamada);
                        bulunamazsa Güncelle düğmesi mağaza adresini açar. iOS'ta mağaza adresi açılır. Mağazada yeni sürüm yayımlanmadan son sürümü artırmayın.
                    </div>

                    <Box flexDirection="row" justifyContent="flex-end" className="mt-6">
                        <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                    </Box>
                </Content>
            </Box>
        </React.Fragment>
    )
}

export default MobilSurumAyar;
